import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Input, Button } from '@redpanda/ui';
import ImageUploader from './ImageUploader';
import { useTranslation } from 'react-i18next';

interface Service {
  id: string;
  storeId: string;
  name: string;
  description?: string;
  price: number;
  duration: number;
  category?: string;
  image?: string;
  slotCapacity?: number;
  isActive: boolean;
}

interface ServiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  service?: Service | null;
  storeId?: string;
}

interface FormData {
  name: string;
  description?: string;
  price: number;
  duration: number;
  category?: string;
  image?: string;
  slotCapacity?: number;
}

const ServiceDialog: React.FC<ServiceDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  service,
  storeId,
}) => {
  const { t } = useTranslation(['pages', 'translation']);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>();

  const [imageUrl, setImageUrl] = useState<string>('');

  const isEditMode = !!service;

  // 設置表單初始值
  useEffect(() => {
    if (isOpen) {
      if (service) {
        // 編輯模式
        setValue('name', service.name);
        setValue('description', service.description || '');
        setValue('price', service.price);
        setValue('duration', service.duration);
        setValue('category', service.category || '');
        setValue('slotCapacity', service.slotCapacity || 1);
        setImageUrl(service.image || '');
      } else {
        // 新增模式
        reset();
        setImageUrl('');
      }
    }
  }, [isOpen, service, setValue, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleFormSubmit = (data: FormData) => {
    // 立即關閉 Dialog（樂觀更新，API 在背景執行）
    handleClose();
    onSubmit({ ...data, image: imageUrl || undefined }).catch(() => {
      // 錯誤處理與 UI 回滾在 store 中完成
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
          {/* 標題列 */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? t('dialogs.service.editTitle', { ns: 'pages' }) : t('dialogs.service.addTitle', { ns: 'pages' })}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* 表單內容 */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-4">

            {/* 服務名稱 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.service.serviceName', { ns: 'pages' })} <span className="text-red-500">*</span>
              </label>
              <Input
                {...register('name', { required: t('dialogs.service.serviceNameRequired', { ns: 'pages' }) })}
                variant={errors.name ? 'error' : 'default'}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* 服務說明 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.service.description', { ns: 'pages' })}
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>

            {/* 價格 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.service.price', { ns: 'pages' })} <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                step="1"
                {...register('price', {
                  required: t('dialogs.service.priceRequired', { ns: 'pages' }),
                  min: { value: 0, message: t('dialogs.service.priceNonNegative', { ns: 'pages' }) },
                  validate: value => Number.isInteger(Number(value)) || t('dialogs.service.priceInteger', { ns: 'pages' }),
                })}
                variant={errors.price ? 'error' : 'default'}
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              {errors.price && (
                <p className="mt-1 text-sm text-red-500">{errors.price.message}</p>
              )}
            </div>

            {/* 服務時長 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.service.duration', { ns: 'pages' })} <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                {...register('duration', {
                  required: t('dialogs.service.durationRequired', { ns: 'pages' }),
                  min: { value: 1, message: t('dialogs.service.durationMin', { ns: 'pages' }) },
                })}
                variant={errors.duration ? 'error' : 'default'}
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              {errors.duration && (
                <p className="mt-1 text-sm text-red-500">{errors.duration.message}</p>
              )}
            </div>

            {/* 時段容量 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.service.slotCapacity', { ns: 'pages' })}
              </label>
              <Input
                type="number"
                {...register('slotCapacity', {
                  setValueAs: v => v === '' || v === null || v === undefined ? undefined : parseInt(v),
                  min: { value: 1, message: t('dialogs.service.slotCapacityMin', { ns: 'pages' }) },
                  validate: value => value === undefined || Number.isInteger(value) || t('dialogs.service.slotCapacityInteger', { ns: 'pages' }),
                })}
                variant={errors.slotCapacity ? 'error' : 'default'}
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              {errors.slotCapacity && (
                <p className="mt-1 text-sm text-red-500">{errors.slotCapacity.message}</p>
              )}
            </div>

            {/* 類別 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.service.category', { ns: 'pages' })}
              </label>
              <Input
                {...register('category')}
              />
            </div>

            {/* 服務圖片 */}
            {storeId && (
              <ImageUploader
                currentImageUrl={imageUrl}
                onImageUploaded={setImageUrl}
                onImageRemoved={() => setImageUrl('')}
                folder="services"
                label={t('dialogs.service.serviceImage', { ns: 'pages' })}
                helpText={t('dialogs.service.uploadImage', { ns: 'pages' })}
              />
            )}

            {/* 操作按鈕 */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                onClick={handleClose}
                variant="secondary"
              >
                {t('dialogs.service.cancel', { ns: 'pages' })}
              </Button>
              <Button
                type="submit"
                className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
              >
                {isEditMode ? t('dialogs.service.update', { ns: 'pages' }) : t('dialogs.service.add', { ns: 'pages' })}
              </Button>
            </div>
          </form>
        </div>
    </div>
  );
};

export default ServiceDialog;
